/**
 * @file useGlobalKeyboardShortcuts.ts
 * @description Global keyboard shortcuts for application navigation
 *
 * Shortcuts:
 * - Tab: Toggle keyboard navigation mode (when enabled)
 * - /: Navigate to home
 * - Cmd+K: Open search
 * - Cmd+B: Toggle browse menu
 * - Cmd+U: Toggle user profile menu
 * - Escape: Close menus / exit navigation mode
 */

import { useEffect } from "react";
import { useKeyboardNavigation } from "../contexts/KeyboardNavigationContext";

interface UseGlobalKeyboardShortcutsOptions {
  readonly onOpenSearch: () => void;
  readonly onToggleBrowseMenu: () => void;
  readonly onToggleUserMenu: () => void;
  readonly onNavigateHome: () => void;
}

export function useGlobalKeyboardShortcuts({
  onOpenSearch,
  onToggleBrowseMenu,
  onToggleUserMenu,
  onNavigateHome,
}: UseGlobalKeyboardShortcutsOptions): void {
  const { isNavigationMode, isNavigationDisabled, toggleNavigationMode } = useKeyboardNavigation();

  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      // Ignore shortcuts when typing in inputs
      const target = e.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) {
        return;
      }

      // Tab: Toggle navigation mode (unless disabled for this route)
      if (e.key === "Tab" && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
        // Don't intercept Tab if navigation is disabled for this route
        if (isNavigationDisabled) {
          return; // Let browser handle Tab normally
        }

        // Don't intercept Tab if a dropdown menu is open
        const hasOpenMenu = document.querySelector('[role="menu"]') !== null;
        if (!hasOpenMenu) {
          e.preventDefault();
          toggleNavigationMode();
        }
        return;
      }

      // /: Navigate to home (like YouTube)
      if (e.key === "/" && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        onNavigateHome();
        return;
      }

      // Cmd+K: Open search
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        onOpenSearch();
        return;
      }

      // Cmd+B: Toggle browse menu
      if ((e.metaKey || e.ctrlKey) && e.key === "b") {
        e.preventDefault();
        onToggleBrowseMenu();
        return;
      }

      // Cmd+U: Toggle user profile menu
      if ((e.metaKey || e.ctrlKey) && e.key === "u") {
        e.preventDefault();
        onToggleUserMenu();
      }
    };

    document.addEventListener("keydown", handleGlobalKeyDown);
    return () => document.removeEventListener("keydown", handleGlobalKeyDown);
  }, [
    isNavigationMode,
    isNavigationDisabled,
    toggleNavigationMode,
    onOpenSearch,
    onToggleBrowseMenu,
    onToggleUserMenu,
    onNavigateHome,
  ]);
}
